import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  User,
  browserLocalPersistence,
  setPersistence,
  onAuthStateChanged,
  AuthError
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, storage, db } from '../config/firebase';
import { convertTimestampToDate } from '../utils/dateUtils';

// Character limits by subscription
const SUBSCRIPTION_LIMITS = {
  free: 10000,      // 10,000 characters per month
  standard: 100000, // 100,000 characters per month
  premium: 250000   // 250,000 characters per month
};

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  role: 'user' | 'admin';
  subscription: 'free' | 'standard' | 'premium';
  charactersUsed: number;
  charactersLimit: number;
  credits: number; // Added credits property for credit-based system
  creditsPurchased?: number; // Total credits ever purchased
  creditsExpiry?: string | null; // Optional expiry date for credits
  createdAt: string | null;
  lastLoginAt: string | null;
  lastGenerationAt: string | null;
  lastLoginIp: string;
  registrationIp: string;
  isBlocked?: boolean;
}

const checkIfUserBlocked = async (uid: string): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data().isBlocked === true;
    }
    return false;
  } catch (error) {
    console.error('Error checking user blocked status:', error);
    return false;
  }
};

// Email/Password Registration
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string
): Promise<UserProfile> => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Get IP address using a service
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const { ip } = await ipResponse.json();

    const timestamp = serverTimestamp();
    const userProfile: UserProfile = {
      uid: user.uid,
      email: user.email!,
      displayName,
      photoURL: user.photoURL,
      emailVerified: false,
      role: 'user' as const,
      subscription: 'free',
      charactersUsed: 0,
      charactersLimit: SUBSCRIPTION_LIMITS.free,
      credits: SUBSCRIPTION_LIMITS.free, // Initialize with free credits
      creditsPurchased: 0,
      creditsExpiry: null, // Credits don't expire
      createdAt: convertTimestampToDate(timestamp),
      lastLoginAt: convertTimestampToDate(timestamp),
      lastGenerationAt: null,
      lastLoginIp: ip,
      registrationIp: ip,
      isBlocked: false
    };

    // Create user profile first
    await setDoc(doc(db, 'users', user.uid), userProfile);

    // Then update profile and send verification email
    await Promise.all([
      updateProfile(user, { displayName }),
      sendEmailVerification(user, {
        url: `${window.location.origin}/dashboard`,
        handleCodeInApp: false
      })
    ]);

    // Set up listener for email verification
    const unsubscribe = onAuthStateChanged(auth, async (updatedUser) => {
      if (updatedUser?.uid === user.uid && updatedUser.emailVerified) {
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            emailVerified: true
          });
        } catch (error) {
          console.error('Error updating email verification status:', error);
        }
        unsubscribe();
      }
    });

    return userProfile;
  } catch (error: any) {
    console.error('Registration error:', error);
    throw new Error(
      error.code === 'auth/email-already-in-use'
        ? 'This email is already registered'
        : error.message
    );
  }
};

// Email/Password Login
export const loginWithEmail = async (
  email: string,
  password: string
): Promise<UserProfile> => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    
    // Check if user is blocked
    const isBlocked = await checkIfUserBlocked(user.uid);
    if (isBlocked) {
      await signOut(auth);
      throw new Error('Your account has been blocked. Please contact support for assistance.');
    }
    
    // Get IP address
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const { ip } = await ipResponse.json();
    
    // Update lastLoginAt and IP
    await updateDoc(doc(db, 'users', user.uid), {
      lastLoginAt: serverTimestamp(),
      lastLoginIp: ip,
      emailVerified: user.emailVerified
    });
    
    return await getUserProfile(user.uid);
  } catch (error: any) {
    if (error.message.includes('blocked')) {
      throw error;
    }
    throw new Error(
      error.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : error.message
    );
  }
};

// Google Sign In
export const signInWithGoogle = async (): Promise<UserProfile> => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    const { user } = await signInWithPopup(auth, googleProvider);

    // Check if user is blocked
    const isBlocked = await checkIfUserBlocked(user.uid);
    if (isBlocked) {
      await signOut(auth);
      throw new Error('Your account has been blocked. Please contact support for assistance.');
    }

    const userDoc = doc(db, 'users', user.uid);
    const userSnapshot = await getDoc(userDoc);
    
    // Get IP address
    const ipResponse = await fetch('https://api.ipify.org?format=json');
    const { ip } = await ipResponse.json();
    
    const timestamp = serverTimestamp();

    if (!userSnapshot.exists()) {
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        role: 'user' as const,
        subscription: 'free',
        charactersUsed: 0,
        charactersLimit: SUBSCRIPTION_LIMITS.free,
        credits: SUBSCRIPTION_LIMITS.free, // Initialize with free credits
        creditsPurchased: 0,
        creditsExpiry: null, // Credits don't expire
        createdAt: convertTimestampToDate(timestamp),
        lastLoginAt: convertTimestampToDate(timestamp),
        lastGenerationAt: null,
        lastLoginIp: ip,
        registrationIp: ip,
        isBlocked: false
      };
      await setDoc(userDoc, userProfile);
      return userProfile;
    }

    await updateDoc(userDoc, {
      lastLoginAt: timestamp,
      lastLoginIp: ip,
      emailVerified: user.emailVerified
    });
    return await getUserProfile(user.uid);
  } catch (error: any) {
    if (error.message.includes('blocked')) {
      throw error;
    }
    throw new Error(
      error.code === 'auth/popup-closed-by-user'
        ? 'Sign in cancelled'
        : error.message
    );
  }
};

// Sign Out
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    console.error('Logout error:', error);
    throw new Error('Failed to sign out');
  }
};

// Password Reset
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(
      error.code === 'auth/user-not-found'
        ? 'No account found with this email'
        : error.message
    );
  }
};

// Get User Profile with retries
export const getUserProfile = async (userId: string): Promise<UserProfile> => {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        throw new Error('User profile not found');
      }

      const data = userDoc.data();

      // Check if user is blocked
      if (data.isBlocked) {
        await signOut(auth);
        throw new Error('Your account has been blocked. Please contact support for assistance.');
      }

      return {
        ...data,
        uid: userId,
        createdAt: convertTimestampToDate(data.createdAt),
        lastLoginAt: convertTimestampToDate(data.lastLoginAt),
        lastGenerationAt: convertTimestampToDate(data.lastGenerationAt)
      } as UserProfile;
    } catch (error: any) {
      if (error.message.includes('blocked')) {
        throw error;
      }
      
      console.error(`Error fetching user profile (attempt ${attempt}/${maxRetries}):`, error);
      
      if (attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
    }
  }

  throw new Error('Failed to fetch user profile after multiple attempts');
};

// Resend Verification Email
export const resendVerificationEmail = async (user: User): Promise<void> => {
  try {
    if (!user) {
      throw new Error('No user provided');
    }

    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }

    // Check if user is blocked
    const isBlocked = await checkIfUserBlocked(user.uid);
    if (isBlocked) {
      throw new Error('Your account has been blocked. Please contact support for assistance.');
    }

    // Add a small delay to prevent rapid requests
    await new Promise(resolve => setTimeout(resolve, 1000));

    await sendEmailVerification(user, {
      url: `${window.location.origin}/dashboard`,
      handleCodeInApp: false
    });
  } catch (error: any) {
    console.error('Error sending verification email:', error);
    
    // Handle specific Firebase auth errors
    if (error instanceof Error && 'code' in error) {
      switch ((error as AuthError).code) {
        case 'auth/too-many-requests':
          throw new Error('Too many attempts. Please try again later.');
        case 'auth/invalid-email':
          throw new Error('Invalid email address.');
        case 'auth/user-not-found':
          throw new Error('User not found.');
        case 'auth/unauthorized-continue-uri':
          throw new Error('Please try again. If the problem persists, contact support.');
        default:
          throw new Error('Failed to send verification email. Please try again.');
      }
    }
    
    throw error;
  }
};